import { JsonSchemaEnum } from '@/business-components/json-schema-enum/JsonSchemaEnum';
import { Card, Popover } from 'aui';
import classNames from 'clsx';
import isEmpty from 'lodash-es/isEmpty';
import React, { useCallback, useMemo } from 'react';
import { JsonSchemaViewerCopyableName } from '../../../../components/JsonSchemaViewer/components/PropertyNameRender';
import { MarkdownSafeMdxViewerLocaleContext } from '../../../../components/MarkdownSafeMdxViewer/MarkdownSafeMdxViewerLocaleContext';
import { formatMessageWithLocale } from '../../../../locales';
import CopyableText from '../../../CopyableTextKit';
import { JsonSchemaViewer } from '../../../JsonSchemaViewer/JsonSchemaViewer';
import { MarkdownOpenExternalLinkCtxCustomer } from '../../ctx/OpenExternalLinkCtx';
import MarkdownViewer from '../../viewer';
import type { MdComponentIndexProps } from '../type';
import styles from './DataSchemaViewer.module.less';
import { useSchema } from './useSchema';

type JSONSchema = any;

export function DataSchemaViewerFactory({
  locale,
  appLowerName,
  theme,
}: Pick<React.ComponentProps<typeof MarkdownViewer>, 'locale' | 'appLowerName' | 'theme'>) {
  const DataSchemaViewer: React.FC<
    {
      title?: string;
      id: string;
      hideName?: boolean | string;
    } & MdComponentIndexProps
  > = function DataSchemaViewer({ hideName, title, id: schemaId, ...props }) {
    const localeContext = React.useContext(MarkdownSafeMdxViewerLocaleContext);
    const newLocale = locale || localeContext;
    const { schema, title: schemaTitle } = useSchema(schemaId);
    const propertyNameRender = JsonSchemaViewerCopyableName;
    const keyValueRenderMap = useMemo(
      () => ({
        enum: (
          values: string[],
          defaultValueNode: React.ReactNode,
          enumDescriptions?: JSONSchema['x-app-enum'],
        ) => {
          if (
            !enumDescriptions ||
            isEmpty(enumDescriptions) ||
            enumDescriptions.every((res: any) => res.value === '')
          ) {
            return defaultValueNode;
          }
          return <JsonSchemaEnum name={defaultValueNode} enumDescriptions={enumDescriptions} />;
        },
      }),
      [],
    );

    // 正因为使用了自定义的MarkdownViewer 可以移除内置的 @stoplight/markdown-viewer
    const descriptionRender = useCallback((value: string) => {
      // 注意这个MarkdownViewer 不支持自定义组件
      return (
        <MarkdownOpenExternalLinkCtxCustomer
          fn={(data) => {
            return {
              openExternalLink: data.openExternalLink,
              imageReplaceHostOptions: data.imageReplaceHostOptions,
              imageSrcAddParams: data.imageSrcAddParams,
              renderExternalLink: data.renderExternalLink,
            };
          }}
        >
          {({
            openExternalLink,
            imageReplaceHostOptions,
            imageSrcAddParams,
            renderExternalLink,
          }) => {
            return (
              <MarkdownViewer
                value={value}
                renderExternalLink={renderExternalLink}
                openExternalLink={openExternalLink}
                imageReplaceHostOptions={imageReplaceHostOptions}
                imageSrcAddParams={imageSrcAddParams}
                locale={locale}
                enableDataSchema={false}
                enableHeadingSlugId={false}
                enableHeadingAutoLink={false}
                showErrorMsg={false}
                isSupportHash={false}
                appLowerName={appLowerName}
                theme={theme}
              />
            );
          }}
        </MarkdownOpenExternalLinkCtxCustomer>
      );
    }, []);

    return (
      <div
        data-markdown-component="x-dataschema-container"
        data-first={props['data-first']}
        data-last={props['data-last']}
      >
        <Card
          size="small"
          title={
            hideName === 'false' || hideName === false || hideName === undefined
              ? (schemaTitle ?? title)
              : null
          }
          className={classNames(styles.parameters__card)}
        >
          <div className="pl-4">
            <JsonSchemaViewer
              schema={schema}
              locale={locale}
              disableCrumbs
              className={styles['json-schema-viewer']}
              propertyNameRender={propertyNameRender}
              keyValueRenderMap={keyValueRenderMap}
              viewMode="standalone"
              descriptionRender={descriptionRender}
              isStateRightAligned={true}
              enableDescriptionShowInTitle
              isMergeAllOf
              appNameLower={appLowerName}
            />
          </div>
        </Card>
      </div>
    );
  };

  (
    DataSchemaViewer as React.FC<{
      title?: string;
      id: string;
    }> & {
      attributes: string[];
    }
  ).attributes = ['title', 'id', 'hideName'];
  return DataSchemaViewer as React.FC<{
    title?: string;
    id: string;
  }> & {
    attributes: string[];
  };
}
